import React, { useState, useEffect } from 'react';
import { createPlantOrder } from '../../services/plantOrderService';
import {
  getCountries,
  getStates,
  getCities,
  getLocations,
} from '../../services/locationService';
import { getOccasions } from '../../services/occasionService';
import { getRelations } from '../../services/relationService';
import { getPlans } from '../../services/planService';
import { getPlants } from '../../services/plantService';

function NewOrderPopup({ onClose, onOrderCreated }) {
  const [formData, setFormData] = useState({
    quantity: '',
    total_price: '',
    current_status: 'order_placed',
    plant_id: '',
    user_id: '', // Replace with actual user ID retrieval
    country_id: '',
    state_id: '',
    city_id: '',
    location_id: '',
    occasion_id: '',
    relation_id: '',
    plan_id: '',
  });

  const [plants, setPlants] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [locations, setLocations] = useState([]);
  const [occasions, setOccasions] = useState([]);
  const [relations, setRelations] = useState([]);
  const [plans, setPlans] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch initial data
  useEffect(() => {
    getPlants().then(response => setPlants(response.data)).catch(console.error);
    getCountries().then(response => setCountries(response.data)).catch(console.error);
    getOccasions().then(response => setOccasions(response.data)).catch(console.error);
    getRelations().then(response => setRelations(response.data)).catch(console.error);
    getPlans().then(response => setPlans(response.data)).catch(console.error);
    getStates().then(response => setStates(response.data)).catch(console.error);
    getCities().then(response => setCities(response.data)).catch(console.error);
    getLocations().then(response => setLocations(response.data)).catch(console.error);

    // Set user_id (replace with actual user ID retrieval)
    const currentUserId = 1; // Replace with actual user ID
    setFormData(prevData => ({ ...prevData, user_id: currentUserId }));
  }, []);

  useEffect(() => {
    if (formData.plan_id && formData.quantity) {
      const selectedPlan = plans.find(plan => plan.plan_id === parseInt(formData.plan_id, 10));
      if (selectedPlan) {
        const totalPrice = parseFloat(selectedPlan.price) * formData.quantity;
        setFormData(prevData => ({
          ...prevData,
          total_price: totalPrice.toFixed(2),
        }));
      }
    } else {
      setFormData(prevData => ({ ...prevData, total_price: '' }));
    }
  }, [formData.plan_id, formData.quantity, plans]);

  const handleChange = e => {
    const { name, value } = e.target;
    let newValue = value;

    // Parse IDs and quantities as numbers
    const numberFields = [
      'quantity',
      'plant_id',
      'user_id',
      'country_id',
      'state_id',
      'city_id',
      'location_id',
      'occasion_id',
      'relation_id',
      'plan_id',
    ];
    if (numberFields.includes(name)) {
      newValue = value ? parseInt(value, 10) : '';
    }

    setFormData({ ...formData, [name]: newValue });
  };

  const handleSubmit = e => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    createPlantOrder(formData)
      .then(response => {
        console.log('Order created:', response.data);
        setLoading(false);
        onClose();
        onOrderCreated();
      })
      .catch(error => {
        console.error(error);
        setError('Failed to create order.');
        setLoading(false);
      });
  };

  // Filter states based on selected country
  const filteredStates = states.filter(state => state.country && state.country.country_id === formData.country_id);
  // Filter cities based on selected state
  const filteredCities = cities.filter(city => city.state && city.state.state_id === formData.state_id);
  // Filter locations based on selected city
  const filteredLocations = locations.filter(location => location.city && location.city.city_id === formData.city_id);

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      {/* Overlay */}
      <div className="fixed inset-0 bg-black opacity-60"></div>

      {/* Popup Content */}
      <div className="relative bg-gray-800 text-gray-200 p-6 rounded-lg shadow-lg z-50 w-full max-w-4xl max-h-screen overflow-y-auto">
        <button
          className="absolute top-2 right-2 text-gray-400 hover:text-gray-100"
          onClick={onClose}
        >
          ✕
        </button>
        <h2 className="mb-6 text-xl font-bold text-indigo-400">Place New Order</h2>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Quantity */}
          <div>
            <label htmlFor="quantity" className="block text-sm font-medium text-gray-300">Quantity</label>
            <input
              type="number"
              name="quantity"
              id="quantity"
              value={formData.quantity}
              onChange={handleChange}
              className="mt-1 p-2 w-full bg-gray-700 text-gray-200 border border-gray-600 rounded focus:ring-indigo-500 focus:border-indigo-500"
              required
            />
          </div>

          {/* Plant */}
          <div>
            <label htmlFor="plant_id" className="block text-sm font-medium text-gray-300">Plant</label>
            <select
              name="plant_id"
              id="plant_id"
              value={formData.plant_id}
              onChange={handleChange}
              className="mt-1 p-2 w-full bg-gray-700 text-gray-200 border border-gray-600 rounded focus:ring-indigo-500 focus:border-indigo-500"
              required
            >
              <option value="">Select a plant</option>
              {plants.map(plant => (
                <option key={plant.plant_id} value={plant.plant_id}>
                  {plant.name}
                </option>
              ))}
            </select>
          </div>

          {/* Country */}
          <div>
            <label htmlFor="country_id" className="block text-sm font-medium text-gray-300">Country</label>
            <select
              name="country_id"
              id="country_id"
              value={formData.country_id}
              onChange={handleChange}
              className="mt-1 p-2 w-full bg-gray-700 text-gray-200 border border-gray-600 rounded focus:ring-indigo-500 focus:border-indigo-500"
              required
            >
              <option value="">Select a country</option>
              {countries.map(country => (
                <option key={country.country_id} value={country.country_id}>
                  {country.name}
                </option>
              ))}
            </select>
          </div>

          {/* State */}
          <div>
            <label htmlFor="state_id" className="block text-sm font-medium text-gray-300">State</label>
            <select
              name="state_id"
              id="state_id"
              value={formData.state_id}
              onChange={handleChange}
              className="mt-1 p-2 w-full bg-gray-700 text-gray-200 border border-gray-600 rounded focus:ring-indigo-500 focus:border-indigo-500"
              required
              disabled={!formData.country_id}
            >
              <option value="">Select a state</option>
              {filteredStates.map(state => (
                <option key={state.state_id} value={state.state_id}>
                  {state.name}
                </option>
              ))}
            </select>
          </div>

          {/* City */}
          <div>
            <label htmlFor="city_id" className="block text-sm font-medium text-gray-300">City</label>
            <select
              name="city_id"
              id="city_id"
              value={formData.city_id}
              onChange={handleChange}
              className="mt-1 p-2 w-full bg-gray-700 text-gray-200 border border-gray-600 rounded focus:ring-indigo-500 focus:border-indigo-500"
              required
              disabled={!formData.state_id}
            >
              <option value="">Select a city</option>
              {filteredCities.map(city => (
                <option key={city.city_id} value={city.city_id}>
                  {city.name}
                </option>
              ))}
            </select>
          </div>

          {/* Location */}
          <div>
            <label htmlFor="location_id" className="block text-sm font-medium text-gray-300">Location</label>
            <select
              name="location_id"
              id="location_id"
              value={formData.location_id}
              onChange={handleChange}
              className="mt-1 p-2 w-full bg-gray-700 text-gray-200 border border-gray-600 rounded focus:ring-indigo-500 focus:border-indigo-500"
              required
              disabled={!formData.city_id}
            >
              <option value="">Select a location</option>
              {filteredLocations.map(location => (
                <option key={location.location_id} value={location.location_id}>
                  {location.area}
                </option>
              ))}
            </select>
          </div>

          {/* Occasion */}
          <div>
            <label htmlFor="occasion_id" className="block text-sm font-medium text-gray-300">Occasion</label>
            <select
              name="occasion_id"
              id="occasion_id"
              value={formData.occasion_id}
              onChange={handleChange}
              className="mt-1 p-2 w-full bg-gray-700 text-gray-200 border border-gray-600 rounded focus:ring-indigo-500 focus:border-indigo-500"
              required
            >
              <option value="">Select an occasion</option>
              {occasions.map(occasion => (
                <option key={occasion.occasion_id} value={occasion.occasion_id}>
                  {occasion.name}
                </option>
              ))}
            </select>
          </div>

          {/* Relation */}
          <div>
            <label htmlFor="relation_id" className="block text-sm font-medium text-gray-300">Relation</label>
            <select
              name="relation_id"
              id="relation_id"
              value={formData.relation_id}
              onChange={handleChange}
              className="mt-1 p-2 w-full bg-gray-700 text-gray-200 border border-gray-600 rounded focus:ring-indigo-500 focus:border-indigo-500"
              required
            >
              <option value="">Select a relation</option>
              {relations.map(relation => (
                <option key={relation.relation_id} value={relation.relation_id}>
                  {relation.name}
                </option>
              ))}
            </select>
          </div>

          {/* Plan */}
          <div>
            <label htmlFor="plan_id" className="block text-sm font-medium text-gray-300">Plan</label>
            <select
              name="plan_id"
              id="plan_id"
              value={formData.plan_id}
              onChange={handleChange}
              className="mt-1 p-2 w-full bg-gray-700 text-gray-200 border border-gray-600 rounded focus:ring-indigo-500 focus:border-indigo-500"
              required
            >
              <option value="">Select a plan</option>
              {plans.map(plan => (
                <option key={plan.plan_id} value={plan.plan_id}>
                  {plan.name} - ${plan.price}
                </option>
              ))}
            </select>
          </div>

          {/* Total Price */}
          <div>
            <label htmlFor="total_price" className="block text-sm font-medium text-gray-300">Total Price</label>
            <input
              type="text"
              name="total_price"
              id="total_price"
              value={formData.total_price}
              className="mt-1 p-2 w-full bg-gray-700 text-gray-200 border border-gray-600 rounded"
              readOnly
            />
          </div>

          {/* Current Status */}
          <div>
            <label htmlFor="current_status" className="block text-sm font-medium text-gray-300">Current Status</label>
            <input
              type="text"
              name="current_status"
              id="current_status"
              value={formData.current_status}
              className="mt-1 p-2 w-full bg-gray-700 text-gray-200 border border-gray-600 rounded"
              readOnly
            />
          </div>

          {/* Error Message */}
          {error && <p className="text-red-600 col-span-2">{error}</p>}

          {/* Submit Button */}
          <div className="col-span-2">
            <button
              type="submit"
              className="w-full mt-4 px-4 py-2 font-bold text-white bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 rounded"
              disabled={loading}
            >
              {loading ? 'Submitting...' : 'Submit Order'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default NewOrderPopup;
